import React from 'react';
import clsx from 'clsx';
import { getInitials } from '../../utils/helpers';

/**
 * Avatar Component
 * 
 * Display user or professional avatar
 * 
 * @param {string} name - Full name for initials
 * @param {string} src - Image URL
 * @param {string} size - 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
 * @param {string} variant - 'circle' | 'square'
 * @param {string} className - Additional CSS classes
 */
const Avatar = ({
  name = '',
  src = null,
  size = 'md',
  variant = 'circle',
  className = '',
  ...props
}) => {
  const sizes = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-12 h-12 text-base',
    lg: 'w-16 h-16 text-xl',
    xl: 'w-20 h-20 text-2xl',
    '2xl': 'w-24 h-24 text-3xl',
  };

  const roundedClass = variant === 'circle' ? 'rounded-full' : 'rounded-xl';
  
  const initials = getInitials(name);
  
  // Generate consistent color from name
  const getColorFromName = (name) => {
    const colors = [
      'from-blue-100 to-blue-200 text-blue-700',
      'from-green-100 to-green-200 text-green-700',
      'from-purple-100 to-purple-200 text-purple-700',
      'from-orange-100 to-orange-200 text-orange-700',
      'from-pink-100 to-pink-200 text-pink-700',
      'from-indigo-100 to-indigo-200 text-indigo-700',
    ];
    const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  const classes = clsx(
    sizes[size],
    roundedClass,
    'flex items-center justify-center font-bold flex-shrink-0',
    className
  );

  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className={clsx(classes, 'object-cover')}
        {...props}
      />
    );
  }

  return (
    <div
      className={clsx(classes, 'bg-gradient-to-br', getColorFromName(name))}
      {...props}
    >
      {initials}
    </div>
  );
};

export default Avatar;
